import { useEffect } from 'react';
import { Grid, Typography } from '@mui/material';
import TableCell from '~/components/ListCardTable/TableCell';
import { styles } from '../../../../styles';
import { TClarifyQuestion, TExplanationAnswer } from '../../../../types';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import useGetContragentTypes from '~/modules/identification/hooks/useGetContragentTypes';
import {
  LEGAL_RESIDENT_MULTISELECT_IDS,
  SOLE_TRADER_MULTISELECT_IDS,
  TRANSPORT_ANSWER_IDS,
} from '../../../../constants';
import { useFieldArray, useFormContext } from 'react-hook-form';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import { AnswerComments } from './AnswerComments';
import { cloneDeep } from 'lodash';

interface IClarifyingInfoRowProps {
  question: TClarifyQuestion;
  index: number;
}

export const ClarifyingInfoRow = ({ question, index }: IClarifyingInfoRowProps) => {
  const { isSoleTrader } = useGetContragentTypes();
  const { setValue, getValues } = useFormContext();
  const { fields, replace } = useFieldArray<
    {
      explanation: {
        answers: TAutocompleteOption[];
      }[];
    },
    `explanation.${number}.answers`
  >({
    name: `explanation.${index}.answers`,
  });

  const getParsedOptions = () => {
    const parsedAnswers = question.answers.map((answer) => ({
      label: answer.text,
      value: answer.id.toString(),
    }));
    if (question.id !== 17) {
      return parsedAnswers;
    } else {
      if (fields.length) {
        const answerIds = fields.map((item) => +item.value);

        return parsedAnswers.filter((item) =>
          answerIds.some((field) => TRANSPORT_ANSWER_IDS.includes(+field))
            ? TRANSPORT_ANSWER_IDS.includes(+item.value)
            : !TRANSPORT_ANSWER_IDS.includes(+item.value)
        );
      } else return parsedAnswers;
    }
  };

  const isMultiple = (
    isSoleTrader ? SOLE_TRADER_MULTISELECT_IDS : LEGAL_RESIDENT_MULTISELECT_IDS
  ).includes(question.id);
  const isBottomSpace = isMultiple && !!fields.length;

  useEffect(() => {
    if (!isMultiple && !fields.length) {
      replace([]);
    }
  }, [isMultiple, fields.length, replace]);

  useEffect(() => {
    if (!getValues(`explanation.${index}.questionId`)) {
      setValue(`explanation.${index}.questionId`, question.id);
    }
  }, [question.id]);

  const handleChange = (value: TAutocompleteOption | TAutocompleteOption[] | null) => {
    if (!value || (Array.isArray(value) && !value.length)) {
      replace([]);
      return;
    }

    const defaultComments = question.id === 9 ? [{ RNOKPP: '', fullName: '' }] : [{ general: '' }];
    const valueArray = Array.isArray(value) ? value : [value];

    const newAnswers = valueArray.map((option) => {
      const existing = fields.find(
        (a: TAutocompleteOption) => String(a.value) === String(option.value)
      );

      if (existing) {
        return cloneDeep(existing);
      }

      return {
        value: option.value,
        label: option.label,
        comments: cloneDeep(defaultComments),
      };
    });

    replace(newAnswers);
  };

  return (
    <Grid container sx={styles.row}>
      <TableCell title="№" size={0.5} sx={{ justifyContent: 'center' }}>
        <Typography variant="body2">{index + 1}</Typography>
      </TableCell>
      <TableCell title="Питання" size={3.5}>
        <Typography variant="body2" sx={{ p: 1 }}>
          {question.text}
        </Typography>
      </TableCell>
      <TableCell
        title="Варіант відповіді"
        size={4}
        stylesProps={{ alignItems: 'flex-start', minHeight: 'unset', width: '100%' }}
      >
        <FormAutocomplete
          multiple={isMultiple}
          options={getParsedOptions()}
          fullWidth
          disableFormOnChange
          clearValueOnReset
          errorTouchedOnly
          variant="standard"
          sx={styles.autocomplete}
          onChange={handleChange}
          name={`explanation.${index}.answers`}
          inputProps={{
            maxLength: 20,
            placeholder: `Оберіть варіант${isMultiple ? 'и' : ''} відповіді`,
          }}
        />
      </TableCell>
      <TableCell title="Додаткова інформація" size={4} stylesProps={{ width: '100%' }}>
        <AnswerComments
          isBottomSpace={isBottomSpace}
          explanationIndex={index}
          questionId={question.id}
          answers={fields as (TExplanationAnswer & Record<'id', string>)[]}
        />
      </TableCell>
    </Grid>
  );
};
